import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import { Document, Font } from '@react-pdf/renderer';

import CoverPage from './pages/CoverPage';
import TableOfContents from './pages/TableOfContents';
import CompanyDescription from './pages/CompanyDescription';
import Exhibits from './pages/Exhibits';
import { getHoldingsPageCount } from './pages/exhibits/Exhibit1_Holdings';
import { sanitizePdfText } from '../../../../utils';

import robotoFont from './assets/fonts/Roboto-Regular.ttf';
import robotoMedFont from './assets/fonts/Roboto-Medium.ttf';
import robotoBoldFont from './assets/fonts/Roboto-Black.ttf';
import robotoItalicFont from './assets/fonts/Roboto-Italic.ttf';
import manropeFont from './assets/fonts/Manrope-Regular.ttf';
import manropeLightFont from './assets/fonts/Manrope-Light.ttf';
import manropeMedFont from './assets/fonts/Manrope-Medium.ttf';
import manropeSemiFont from './assets/fonts/Manrope-SemiBold.ttf';
import manropeBoldFont from './assets/fonts/Manrope-Bold.ttf';

export default function PDFRenderer({ reportData: rawReportData }) {
  Font.register({ family: 'Roboto', src: robotoFont });
  Font.register({ family: 'RobotoMed', src: robotoMedFont });
  Font.register({ family: 'RobotoBold', src: robotoBoldFont });
  Font.register({ family: 'RobotoItalic', src: robotoItalicFont });
  Font.register({ family: 'Manrope', src: manropeFont });
  Font.register({ family: 'ManropeLight', src: manropeLightFont });
  Font.register({ family: 'ManropeMed', src: manropeMedFont });
  Font.register({ family: 'ManropeSemi', src: manropeSemiFont });
  Font.register({ family: 'ManropeBold', src: manropeBoldFont });

  Font.registerHyphenationCallback((word) => [word]);

  const sanitizeDeep = (value) => {
    if (typeof value === 'string') return sanitizePdfText(value);
    if (Array.isArray(value)) return value.map(sanitizeDeep);
    if (value && typeof value === 'object') {
      return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, sanitizeDeep(v)]));
    }
    return value;
  };
  const reportData = {
    ...rawReportData,
    footnoteData: sanitizeDeep(rawReportData.footnoteData),
    companyInfo: sanitizeDeep(rawReportData.companyInfo),
  };

  const parseMethodWeight = (weight) => parseFloat(String(weight ?? 0).replace('%', '')) || 0;
  const hasBacksolve = parseMethodWeight(reportData.weights?.backsolve) > 0;
  const hasPubCo = !!reportData.OptionPricingAllocation.pubco;
  const hasValueCompare = !!Object.keys(reportData.ValueComparison).length;

  const getDescriptionPageSplits = (publicCompanyDescriptions) => {
    const splitLength = [0];
    let compIndex = 0;
    const descriptionRows = publicCompanyDescriptions?.['Company Description'] || [];
    while (compIndex < descriptionRows.length) {
      let strLength = 0;
      while (strLength < 4650 && compIndex < descriptionRows.length) {
        strLength += (descriptionRows[compIndex] || '').length;
        compIndex += 1;
      }
      splitLength.push(compIndex);
    }
    return splitLength;
  };

  let sortedBreakpoints = Object.keys(reportData.BreakpointAnalyses.breakpoints).filter((el) => el.includes(' | '))
    .sort((a, b) => parseFloat(a.split(' | ')[1].split('$')[1]) - parseFloat(b.split(' | ')[1].split('$')[1]));

  let sortedOPMBreakpoints = Object.keys(reportData.OptionPricingAllocation.backsolve?.incrementalOptionValue || []).sort((a, b) => {
    if (a === 'All Participate') return 1;
    if (b === 'All Participate') return -1;
    return parseFloat(a.split(' | ')[1].split('$')[2]) - parseFloat(b.split(' | ')[1].split('$')[2]);
  });

  let sortedBacksolveBreakpoints = Object.keys(reportData.OptionPricingAllocation.goalSeek?.incrementalOptionValue || []).sort((a, b) => {
    if (a === 'All Participate') return 1;
    if (b === 'All Participate') return -1;
    return parseFloat(a.split(' | ')[1].split('$')[2]) - parseFloat(b.split(' | ')[1].split('$')[2]);
  });

  // test
  let sortedPubCoBreakpoints = Object.keys(reportData.OptionPricingAllocation.pubco?.incrementalOptionValue || []).sort((a, b) => {
    if (a === 'All Participate') return 1;
    if (b === 'All Participate') return -1;
    return parseFloat(a.split(' | ')[1].split('$')[2]) - parseFloat(b.split(' | ')[1].split('$')[2]);
  });

  function footnotePageCount(startCount, footnoteData, maxLines = 9, charCount = 230) {
    return startCount + (footnoteData ? Math.ceil(footnoteData.length / charCount) : 0) > maxLines ? 1 : 0;
  }

  // Sort comps data alphabetically by company name
  const sortCompsDataAlphabetically = (dataObject) => {
    if (!dataObject || !dataObject.Name || !Array.isArray(dataObject.Name)) {
      return dataObject;
    }
    
    // Create array of objects with original index and company name
    const indexedData = dataObject.Name.map((name, index) => ({ name, index }));
    
    // Sort by company name alphabetically (case-insensitive)
    indexedData.sort((a, b) => a.name.localeCompare(b.name));
    
    // Create new sorted data object
    const sortedData = {};
    Object.keys(dataObject).forEach(key => {
      // Skip non-data fields that shouldn't be sorted
      if (key === 'tableHeaders' || key === 'selectedMarketEquityAdjustment' || 
          key === 'summaryObject' ||
          typeof dataObject[key] === 'string' || typeof dataObject[key] === 'number') {
        sortedData[key] = dataObject[key];
      } else if (Array.isArray(dataObject[key])) {
        sortedData[key] = indexedData.map(item => dataObject[key][item.index]);
      } else {
        sortedData[key] = dataObject[key];
      }
    });
    
    return sortedData;
  };

  // Sort comps data alphabetically for all exhibits
  const sortedValuationDateVolatility = reportData.ValuationDateVolatility ? {
    ...reportData.ValuationDateVolatility,
    volatilityObject: sortCompsDataAlphabetically(reportData.ValuationDateVolatility.volatilityObject)
  } : reportData.ValuationDateVolatility;

  const sortedTransactionDateVolatility = reportData.TransactionDateVolatility ? {
    ...reportData.TransactionDateVolatility,
    volatilityObject: sortCompsDataAlphabetically(reportData.TransactionDateVolatility.volatilityObject)
  } : reportData.TransactionDateVolatility;

  const selectedPubCoNames = new Set(
    reportData.PublicCompanyMethod?.grids?.multiples?.['Company Name'] || [],
  );

  const sortedPublicCompanyDescriptions = (() => {
    const sortedDescriptions = sortCompsDataAlphabetically(reportData.PublicCompanyDescriptions);
    if (!selectedPubCoNames.size || !sortedDescriptions?.Name?.length) return sortedDescriptions;

    const selectedIndexes = sortedDescriptions.Name
      .map((name, index) => (selectedPubCoNames.has(name) ? index : -1))
      .filter((index) => index >= 0);

    const filteredDescriptions = {};
    Object.keys(sortedDescriptions).forEach((key) => {
      if (Array.isArray(sortedDescriptions[key])) {
        filteredDescriptions[key] = selectedIndexes.map((index) => sortedDescriptions[key][index]);
      } else {
        filteredDescriptions[key] = sortedDescriptions[key];
      }
    });
    return filteredDescriptions;
  })();

  const sortedPubCoDescriptions = getDescriptionPageSplits(sortedPublicCompanyDescriptions);

  const sortedMarketEquityAdjustment = sortCompsDataAlphabetically(reportData.MarketEquityAdjustment);

  const sortedPublicCompanyMethod = reportData.PublicCompanyMethod ? {
    ...reportData.PublicCompanyMethod,
    grids: reportData.PublicCompanyMethod.grids ? {
      ...reportData.PublicCompanyMethod.grids,
      multiples: sortCompsDataAlphabetically(reportData.PublicCompanyMethod.grids.multiples)
    } : reportData.PublicCompanyMethod.grids
  } : reportData.PublicCompanyMethod;

  const splitBreakpoints = (breakpoints) => {
    if (breakpoints.length > 13) {
      const splitIndex = Math.ceil(breakpoints.length / 2);
      return [breakpoints.slice(0, splitIndex), breakpoints.slice(splitIndex)];
    }
    return [[...breakpoints]];
  };

  sortedBreakpoints = splitBreakpoints(sortedBreakpoints);
  // 0%-weighted backsolve is not a relied-upon method: suppress Exhibits 6-9.
  sortedOPMBreakpoints = hasBacksolve ? splitBreakpoints(sortedOPMBreakpoints) : [];
  sortedBacksolveBreakpoints = hasBacksolve ? splitBreakpoints(sortedBacksolveBreakpoints) : [];
  sortedPubCoBreakpoints = splitBreakpoints(sortedPubCoBreakpoints);

  const ownershipClasses = reportData.CapitalizationTable['Ownership Class'].length + 2;

  const holdingsPageCount = getHoldingsPageCount(reportData);
  const holdingsStartPage = 4;

  // Create new reportData with sorted comps (needed before page-count helpers that read names)
  const sortedReportData = {
    ...reportData,
    ValuationDateVolatility: sortedValuationDateVolatility,
    TransactionDateVolatility: sortedTransactionDateVolatility,
    PublicCompanyDescriptions: sortedPublicCompanyDescriptions,
    MarketEquityAdjustment: sortedMarketEquityAdjustment,
    PublicCompanyMethod: sortedPublicCompanyMethod
  };

  const compNames = sortedReportData.ValuationDateVolatility?.volatilityObject?.Name || [];
  const volatilityDataPages = Math.ceil(compNames.length / 37) || 1;
  const volatilityOverflowPages = (compNames.slice((volatilityDataPages - 1) * 37).length > 24) ? 1 : 0;
  const volatilityExhibitPages = volatilityDataPages + volatilityOverflowPages;

  const breakpointRows = (reportData.BreakpointAnalyses?.breakpoints?.['Ownership Class']?.length || 0)
    + (reportData.footnoteData?.bpAnalysis ? Math.ceil(String(reportData.footnoteData.bpAnalysis).length / 230) : 0);
  const pagesPerBreakpointExhibit = breakpointRows > 6 ? 3 : 1;

  const opmOwnerCount = (() => {
    const alloc = reportData.OptionPricingAllocation?.backsolve?.incrementalOptionValueAllocation;
    try {
      const parsed = typeof alloc === 'string' ? JSON.parse(alloc) : alloc;
      return parsed?.['Ownership Class']?.length || ownershipClasses;
    } catch {
      return ownershipClasses;
    }
  })();
  const pagesPerOpmExhibit = (footnoteKey) => {
    const footnote = reportData.footnoteData?.[footnoteKey];
    const rowLength = opmOwnerCount + (footnote ? Math.ceil(String(footnote).length / 230) : 0);
    return rowLength > 10 ? 2 : 1;
  };

  const capTablePages = 1 + footnotePageCount(ownershipClasses, reportData.footnoteData.capTable);
  const fairValuePages = 1 + footnotePageCount(ownershipClasses, reportData.footnoteData.fairValuePerShare, 10);
  const pubCoDescPages = hasPubCo ? Math.max(sortedPubCoDescriptions.length - 1, 1) : 0;
  const pubCoMethodPages = hasPubCo ? (volatilityDataPages + volatilityOverflowPages) : 0;
  const pubCoGrowthPages = hasPubCo ? (volatilityDataPages + volatilityOverflowPages) : 0;

  // Running start-page cursor. Each totalPageCount.* value is the START page of that exhibit.
  let nextPage = holdingsStartPage;
  const totalPageCount = {
    holdings: nextPage,
    holdingsPageCount,
  };
  nextPage += holdingsPageCount;

  totalPageCount.equity = nextPage;
  nextPage += 1;

  totalPageCount.capTableStart = nextPage;
  nextPage += capTablePages;
  totalPageCount.capTable = nextPage; // start of first Breakpoint exhibit

  totalPageCount.breakpointStarts = sortedBreakpoints.map(() => {
    const start = nextPage;
    nextPage += pagesPerBreakpointExhibit;
    return start;
  });
  totalPageCount.breakpoints = totalPageCount.breakpointStarts[0] || totalPageCount.capTable;

  totalPageCount.opmStarts = sortedOPMBreakpoints.map(() => {
    const start = nextPage;
    nextPage += pagesPerOpmExhibit('opmAllocation');
    return start;
  });
  // TOC/Exhibits: `breakpoints` key is the OPM Allocation start page (legacy naming).
  totalPageCount.breakpoints = totalPageCount.opmStarts[0] || nextPage;

  totalPageCount.trnBacksolveStarts = sortedBacksolveBreakpoints.map(() => {
    const start = nextPage;
    nextPage += pagesPerOpmExhibit('opmAllocation');
    return start;
  });
  // TOC/Exhibits: `opmBacksolve` key is the Transaction Date Backsolve Method start page.
  totalPageCount.opmBacksolve = totalPageCount.trnBacksolveStarts[0] || nextPage;

  if (hasPubCo) {
    totalPageCount.gpcStarts = sortedPubCoBreakpoints.map(() => {
      const start = nextPage;
      nextPage += pagesPerOpmExhibit('gpcAllocation');
      return start;
    });
    totalPageCount.opmPubCo = totalPageCount.gpcStarts[0] || nextPage;
  } else {
    totalPageCount.gpcStarts = [];
    totalPageCount.opmPubCo = nextPage;
  }

  totalPageCount.fairValue = nextPage;
  nextPage += fairValuePages;

  totalPageCount.valVolatility = nextPage;
  nextPage += volatilityExhibitPages;

  totalPageCount.tranVolatility = nextPage;
  nextPage += volatilityExhibitPages;

  totalPageCount.marketAdjustment = nextPage;
  nextPage += volatilityExhibitPages;

  if (hasPubCo) {
    totalPageCount.pubCoDescriptions = nextPage;
    nextPage += pubCoDescPages;
    totalPageCount.pubCoMethod = nextPage;
    nextPage += Math.max(pubCoMethodPages, 1);
    totalPageCount.pubCoGrowthMargins = nextPage;
    nextPage += Math.max(pubCoGrowthPages, 1);
  } else {
    totalPageCount.pubCoDescriptions = nextPage;
    totalPageCount.pubCoMethod = nextPage;
    totalPageCount.pubCoGrowthMargins = nextPage;
  }

  totalPageCount.generalAppraisersNotes = nextPage;
  nextPage += 1;

  totalPageCount.valSumWeighting = nextPage;
  nextPage += 1;

  if (hasBacksolve) {
    totalPageCount.valSumBacksolve = nextPage;
    nextPage += 1;
  } else {
    totalPageCount.valSumBacksolve = nextPage;
  }

  totalPageCount.allocationMethodSummary = nextPage;
  nextPage += 1;

  totalPageCount.valueComparison = nextPage;

  // Calculate dynamic exhibit numbers
  let currentExhibitNum = 1;

  const exhibitNumber = {
    fundHolding: currentExhibitNum,
    equityValue: currentExhibitNum + 1,
    capTable: currentExhibitNum + 2,
  };

  currentExhibitNum += 3;

  // Breakpoints - create array of numbers, one for each breakpoint
  exhibitNumber.breakpoints = sortedBreakpoints.map(() => {
    const num = currentExhibitNum;
    currentExhibitNum += 1;
    return num;
  });

  // OPM Allocations - create array of numbers, one for each OPM
  exhibitNumber.opmBacksolve = sortedOPMBreakpoints.map(() => {
    const num = currentExhibitNum;
    currentExhibitNum += 1;
    return num;
  });

  // Backsolve Methods - create array of numbers, one for each backsolve
  exhibitNumber.trnDateBacksolveMethod = sortedBacksolveBreakpoints.map(() => {
    const num = currentExhibitNum;
    currentExhibitNum += 1;
    return num;
  });

  // GPC Allocations - create array of numbers, one for each GPC (only if hasPubCo)
  if (hasPubCo) {
    exhibitNumber.gpcOPMAllocation = sortedPubCoBreakpoints.map(() => {
      const num = currentExhibitNum;
      currentExhibitNum += 1;
      return num;
    });
  }

  // Continue with remaining single exhibits
  exhibitNumber.fairValue = currentExhibitNum;
  currentExhibitNum += 1;
  exhibitNumber.valVolatility = currentExhibitNum;
  currentExhibitNum += 1;
  exhibitNumber.tranVolatility = currentExhibitNum;
  currentExhibitNum += 1;
  exhibitNumber.marketAdjustment = currentExhibitNum;
  currentExhibitNum += 1;

  if (hasPubCo) {
    exhibitNumber.pubCoDescriptions = currentExhibitNum;
    currentExhibitNum += 1;
    exhibitNumber.pubCoMethod = currentExhibitNum;
    currentExhibitNum += 1;
    exhibitNumber.pubCoGrowthMargins = currentExhibitNum;
    currentExhibitNum += 1;
  }

  exhibitNumber.generalAppraisersNotes = currentExhibitNum;
  currentExhibitNum += 1;
  exhibitNumber.valSumWeighting = currentExhibitNum;
  currentExhibitNum += 1;

  if (hasBacksolve) {
    exhibitNumber.valSumBacksolve = currentExhibitNum;
    currentExhibitNum += 1;
  }

  exhibitNumber.allocationMethodSummary = currentExhibitNum;
  currentExhibitNum += 1;

  if (hasValueCompare) {
    exhibitNumber.valueComparison = currentExhibitNum;
  }

  const valuationDate = moment(reportData.companyInfo.valuationDate, 'YYYY-MM-DD').format('MMMM DD, YYYY');

  return (
    <Document>
      <CoverPage
        companyName={reportData.companyInfo.companyName}
        clientName={reportData.companyInfo.investorFirmName}
        valuationDate={valuationDate}
        isDraft={reportData.isDraft}
        isVenBioUser={reportData.isVenBioUser}
      />
      <TableOfContents
        valuationDate={valuationDate}
        totalPageCount={totalPageCount}
        reportData={reportData}
        hasPubCo={hasPubCo}
        hasValueCompare={hasValueCompare}
        hasBacksolve={hasBacksolve}
      />
      <CompanyDescription
        reportData={reportData}
        valuationDate={valuationDate}
      />
      <Exhibits
        reportData={sortedReportData}
        sortedBreakpoints={sortedBreakpoints}
        sortedOPMBreakpoints={sortedOPMBreakpoints}
        sortedBacksolveBreakpoints={sortedBacksolveBreakpoints}
        sortedPubCoBreakpoints={sortedPubCoBreakpoints}
        sortedPubCoDescriptions={sortedPubCoDescriptions}
        totalPageCount={totalPageCount}
        hasValueCompare={hasValueCompare}
        hasBacksolve={hasBacksolve}
        hasPubCo={hasPubCo}
        exhibitNumber={exhibitNumber}
      />
    </Document>
  );
}


PDFRenderer.propTypes = { reportData: PropTypes.object.isRequired };
